/**
 * PlanFolderRow · 文件夹方案预览的单行节点 (纯 UI)
 *
 * 无副作用 / 不读 store / 不调接口, 全靠 props 驱动。
 * 递归渲染由本组件自身实现 (children 非空时下钻渲染同组件)。
 *
 * 行为规则:
 *   · 反选标识用 node.excludeKey (后端文件夹 key), 不再用路径
 *   · 祖先级联: excluded = selfExcluded || ancestorExcluded, ancestorExcluded 由父行沿树传入
 *   · checkboxDisabled 取决于:
 *       1) 当前 mode 为只读 (live_readonly / history_readonly)
 *       2) 祖先节点被排除 (整子树跟随祖先, 本行禁用避免冗余操作)
 *   · 展开 caret 只在 hasChildren 时显示, 点击触发 onToggleExpand
 *   · loading 子层时显示 inline spin
 */

import React from 'react'
import { Checkbox, Spin } from 'antd'
import classNames from 'classnames'
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons'
import type { FolderPlanPreviewMode, PlanPreviewNode } from './types'
import FolderIconSvg from '../FolderList/FolderIconSvg'
import styles from './FolderPlanPreview.module.scss'

export interface PlanFolderRowProps {
  node: PlanPreviewNode
  depth: number
  mode: FolderPlanPreviewMode
  expandedKeys: Set<string>
  loadingKeys: Set<string>
  excludedKeys: Set<string>
  /** 祖先链路上是否有任一已 excluded · 影响 checkbox 禁用 */
  ancestorExcluded: boolean
  onToggleExpand: (key: string, node: PlanPreviewNode) => void
  onToggleExclude: (key: string) => void
}

const PlanFolderRow: React.FC<PlanFolderRowProps> = ({
  node,
  depth,
  mode,
  expandedKeys,
  loadingKeys,
  excludedKeys,
  ancestorExcluded,
  onToggleExpand,
  onToggleExclude
}) => {
  const expanded = expandedKeys.has(node.key)
  const selfExcluded = excludedKeys.has(node.excludeKey)
  // 自身被反选, 或祖先被反选 (整子树跟随祖先, 级联态由父行沿树传入 ancestorExcluded)
  const excluded = selfExcluded || ancestorExcluded
  const canToggleExclude = mode === 'live' || mode === 'history_editable'
  // 祖先已排除时本行禁用 (避免对已随祖先排除的子树做冗余勾选)
  const checkboxDisabled = !canToggleExclude || ancestorExcluded
  const loadingChildren = loadingKeys.has(node.key)

  return (
    <div>
      <div
        className={classNames(styles.row, {
          [styles.rowExcluded]: excluded,
          [styles.rowChild]: depth > 0
        })}
        style={{ paddingLeft: 16 + depth * 18 }}
      >
        {/* 只读态（live_readonly / history_readonly）不渲染 checkbox —— 只读没有
            勾选语义；被排除的行仍靠 .rowExcluded 的置灰表达。可编辑态才渲染。 */}
        {canToggleExclude && (
          <span
            className={styles.checkbox}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={!excluded}
              disabled={checkboxDisabled}
              onChange={() => onToggleExclude(node.excludeKey)}
            />
          </span>
        )}
        <span
          className={styles.expander}
          onClick={() => onToggleExpand(node.key, node)}
          style={{ cursor: node.hasChildren ? 'pointer' : 'default' }}
        >
          {node.hasChildren &&
            (expanded ? (
              <CaretDownOutlined style={{ fontSize: 10, color: '#86909c' }} />
            ) : (
              <CaretRightOutlined style={{ fontSize: 10, color: '#86909c' }} />
            ))}
        </span>
        {/* 智能文件夹图标 · 复用文件夹列表的 FolderIconSvg · 黄色底 + 右下角刷新水印
            （预览的都是 AI 将要创建的文件夹，故 isAi 恒为 true）；
            hasChildren 沿用列表"含内容"露纸角样式 */}
        <span className={styles.folderIcon}>
          <FolderIconSvg
            variant="manual"
            isAi
            size={20}
            hasChildren={node.hasChildren}
          />
        </span>
        <span className={styles.name} title={node.name}>
          {node.name}
        </span>
        {node.hasChildren && node.childCount !== undefined && (
          <span className={styles.childCount}>{node.childCount} 子文件夹</span>
        )}
      </div>
      {expanded && node.hasChildren && (
        <div>
          {renderChildrenSection({
            node,
            depth,
            loadingChildren,
            ancestorExcluded: excluded,
            mode,
            expandedKeys,
            loadingKeys,
            excludedKeys,
            onToggleExpand,
            onToggleExclude
          })}
        </div>
      )}
    </div>
  )
}

interface RenderChildrenArgs {
  node: PlanPreviewNode
  depth: number
  loadingChildren: boolean
  ancestorExcluded: boolean
  mode: FolderPlanPreviewMode
  expandedKeys: Set<string>
  loadingKeys: Set<string>
  excludedKeys: Set<string>
  onToggleExpand: (key: string, node: PlanPreviewNode) => void
  onToggleExclude: (key: string) => void
}

/**
 * 渲染子节点区域 · 三态:
 *   · loadingChildren = true   → spin
 *   · node.children === undefined → 占位 spin (尚未发起加载, 但 expanded 命中)
 *   · node.children = []       → 不渲染 (hasChildren=true 但实际无子, 罕见)
 *   · node.children non-empty  → 递归渲染
 */
function renderChildrenSection(args: RenderChildrenArgs): React.ReactNode {
  const {
    node,
    depth,
    loadingChildren,
    ancestorExcluded,
    mode,
    expandedKeys,
    loadingKeys,
    excludedKeys,
    onToggleExpand,
    onToggleExclude
  } = args

  if (loadingChildren || node.children === undefined) {
    return (
      <div
        className={styles.childLoading}
        style={{ paddingLeft: 16 + (depth + 1) * 18 }}
      >
        <Spin size="small" /> <span>加载中…</span>
      </div>
    )
  }
  return node.children.map((child) => (
    <PlanFolderRow
      key={child.key}
      node={child}
      depth={depth + 1}
      mode={mode}
      expandedKeys={expandedKeys}
      loadingKeys={loadingKeys}
      excludedKeys={excludedKeys}
      ancestorExcluded={ancestorExcluded}
      onToggleExpand={onToggleExpand}
      onToggleExclude={onToggleExclude}
    />
  ))
}

export default PlanFolderRow
